import type { UseNavigationOption } from '@actr/hooks';
import { NavigationType, queryStringToRecord, useNavigation } from '@actr/hooks';
import { AccountRoutes } from '@actr/widgets/src/navigation/routes/account';
import { AkV2Routes } from '@actr/widgets/src/navigation/routes/ak-v2';
import config from 'Configs';
import { useCallback } from 'react';

export type RedirectOptions<T extends string> = Omit<UseNavigationOption<T>, 'useRouter'>

const useRedirectTo = () => {
  const { navigate } = useNavigation();

  const toBooking = useCallback((
    options: RedirectOptions<AkV2Routes.AVIA_BOOKING>
  ) => {
    navigate(AkV2Routes.AVIA_BOOKING, {
      ...options,
      useRouter: config.navigation.useRouterFor.redirectToAviaBooking
    });
  }, []);

  const toBookingNative = useCallback((bookUrl?: string) => {
    if (bookUrl) {
      setTimeout(() => {
        window.location.href = bookUrl;
      }, 100);
    } else {
      throw new Error('Book url not found');
    }
  }, []);

  const toCheckout = useCallback((
    options: RedirectOptions<AkV2Routes.AVIA_CHECKOUT>
  ) => {
    navigate(AkV2Routes.AVIA_CHECKOUT, {
      ...options,
      useRouter: config.navigation.useRouterFor.redirectToAviaCheckout
    });
  }, []);

  // в текущем виде не использовать в методах на странице рез-тов, так как ломает фильтры - по дефолту клеит старое с новым
  // TODO переделать на получение опций через аргумент, как в остальных методах
  const toSearch = useCallback((
    queryString: string,
    navigationType: NavigationType = NavigationType.PUSH,
    saveSearchParams = true
  ) => {
    const searchParams = queryStringToRecord(queryString);
    navigate(AkV2Routes.AVIA_SEARCH, { navigationType, searchParams, saveSearchParams });
  }, []);

  const toHome = useCallback((
    options: RedirectOptions<AkV2Routes.HOME>
  ) => {
    navigate(AkV2Routes.HOME, {
      ...options,
      useRouter: config.navigation.useRouterFor.redirectToHome
    });
  }, []);

  const toHomeTravel = useCallback((
    options: RedirectOptions<AkV2Routes.HOME>
  ) => {
    navigate(AkV2Routes.HOME_TRAVEL, options);
  }, []);

  const toPostBooking = useCallback((
    options: RedirectOptions<AkV2Routes.AVIA_POST_BOOKING>
  ) => {
    navigate(AkV2Routes.AVIA_POST_BOOKING, {
      ...options,
      useRouter: config.navigation.useRouterFor.redirectToAviaPostBooking
    });
  }, []);

  const toPostSales = useCallback((
    options: RedirectOptions<AkV2Routes.AVIA_POST_SALES>
  ) => {
    navigate(AkV2Routes.AVIA_POST_SALES, {
      ...options,
    });
  }, []);

  const toCheckoutPostSales = useCallback((
    options: RedirectOptions<AkV2Routes.AVIA_CHECKOUT_POST_SALES>
  ) => {
    navigate(AkV2Routes.AVIA_CHECKOUT_POST_SALES, {
      ...options,
    });
  }, []);

  const toAccount = useCallback((
    options: RedirectOptions<AccountRoutes.ORDERS>
  ) => {
    navigate(AccountRoutes.ORDERS, {
      ...options,
      useRouter: false,
    });
  }, []);

  return {
    toBooking,
    toBookingNative,
    toCheckout,
    toSearch,
    toHome,
    toHomeTravel,
    toPostBooking,
    toPostSales,
    toCheckoutPostSales,
    toAccount
  };
};

export default useRedirectTo;
